import React, { useState, useEffect, useContext } from 'react';
import API_BASE_URL from '../../config';
import { getToken } from '../../utils/tokenUtils';
import { WalletContext } from '../../context/WalletContext';
import ProductDetail from './ProductDetail';
import {
  ShoppingBag,
  History,
  Coins,
  Tag,
  CheckCircle2,
  Clock,
  Loader2,
  AlertTriangle,
  X,
  Copy
} from 'lucide-react';

const ProductCategoryLabels = {
  1: 'Merchandise',
  2: 'Supplement',
  3: 'Accessory',
  4: 'Voucher',
  5: 'Other'
};

const getStockStatusBadge = (status) => {
  switch (status) {
    case 1: // InStock
      return (
        <span
          style={{
            fontSize: 9,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.8,
            padding: "2px 6px",
            borderRadius: 4,
            background: "rgba(16, 185, 129, 0.1)", // emerald-500/10
            border: "1px solid rgba(16, 185, 129, 0.2)",
            color: "#34D399", // emerald-400
          }}
        >
          In Stock
        </span>
      );
    case 2: // OutOfStock
      return (
        <span
          style={{
            fontSize: 9,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.8,
            padding: "2px 6px",
            borderRadius: 4,
            background: "rgba(239, 68, 68, 0.1)", // red-500/10
            border: "1px solid rgba(239, 68, 68, 0.2)",
            color: "#F87171", // red-400
          }}
        >
          Out of Stock
        </span>
      );
    case 3: // LimitedStock
      return (
        <span
          style={{
            fontSize: 9,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.8,
            padding: "2px 6px",
            borderRadius: 4,
            background: "rgba(245, 158, 11, 0.1)", // amber-500/10
            border: "1px solid rgba(245, 158, 11, 0.2)",
            color: "#FBBF24", // amber-400
          }}
        >
          Limited Stock
        </span>
      );
    default:
      return null;
  }
};


const Store = () => {
  const { balance, refreshWallet } = useContext(WalletContext);
  const [activeTab, setActiveTab] = useState('catalog'); // 'catalog' or 'history'
  const [products, setProducts] = useState([]);
  const [history, setHistory] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState('');

  // Modals state
  const [confirmRedeem, setConfirmRedeem] = useState(null); // holds product to redeem
  const [redeemSuccess, setRedeemSuccess] = useState(null); // holds success data
  const [redeeming, setRedeeming] = useState(false);
  const [copied, setCopied] = useState(false);

  // Fetch catalog products
  const fetchProducts = async () => {
    setLoadingProducts(true);
    setError('');
    try {
      const rawToken = getToken();
      let url = `${API_BASE_URL}/api/member/store/products`;
      if (categoryFilter) {
        url += `?category=${categoryFilter}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${rawToken}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to retrieve store products.');
      const data = await response.json();
      setProducts(data);
    } catch (err) {
      console.error(err);
      setError('Could not connect to store. Please try again.');
    } finally {
      setLoadingProducts(false);
    }
  };

  // Fetch redemption history
  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const rawToken = getToken();
      const response = await fetch(`${API_BASE_URL}/api/member/store/redemptions`, {
        headers: {
          'Authorization': `Bearer ${rawToken}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'catalog') {
      fetchProducts();
    } else {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, categoryFilter]);

  const handleRedeem = async () => {
    if (!confirmRedeem || redeeming) return;
    setRedeeming(true);
    setError('');
    try {
      const rawToken = getToken();
      const response = await fetch(`${API_BASE_URL}/api/member/store/redeem`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${rawToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ productId: confirmRedeem.id })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Redemption failed.');
      }

      const successData = await response.json();
      setRedeemSuccess({
        productName: successData.productName,
        redemptionCode: successData.redemptionCode,
        coinsSpent: successData.coinsSpent
      });
      setConfirmRedeem(null);
      refreshWallet();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error processing redemption.');
      setConfirmRedeem(null);
    } finally {
      setRedeeming(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header with Balance */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#2b2b2b] pb-5">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <ShoppingBag className="text-lime-400" size={28} />
            <span>FitScan Store</span>
          </h2>
          <p className="text-xs text-gray-400 mt-1">
            Redeem rewards, fitness apparel, supplements, and vouchers with your hard-earned coins.
          </p>
        </div>

        {/* Balance Display */}
        <div className="flex items-center gap-3 bg-[#181818] border border-[#2b2b2b] px-4 py-2.5 rounded-2xl w-fit">
          <Coins className="text-[#6CD800]" size={20} />
          <div className="flex flex-col">
            <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">My Balance</span>
            <span className="text-base font-black text-white leading-none mt-0.5">
              🪙 {balance.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg text-xs flex items-center gap-2">
          <AlertTriangle size={14} />
          <span>{error}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 bg-[#101010] p-1.5 rounded-xl border border-[#2b2b2b] w-fit">
        <button
          onClick={() => {
            setSelectedProduct(null);
            setActiveTab('catalog');
          }}
          className={`px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'catalog'
            ? 'bg-[#222] text-yellow-400 border border-gray-800'
            : 'text-gray-400 hover:text-white'
            }`}
        >
          <ShoppingBag size={14} /> Shop Catalog
        </button>
        <button
          onClick={() => {
            setSelectedProduct(null);
            setActiveTab('history');
          }}
          className={`px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${activeTab === 'history'
            ? 'bg-[#222] text-yellow-400 border border-gray-800'
            : 'text-gray-400 hover:text-white'
            }`}
        >
          <History size={14} /> Redemptions ({history.length || '0'})
        </button>
      </div>

      {/* Catalog Tab */}
      {activeTab === 'catalog' && (
        <div className="space-y-6">
          {/* Category Filter Pills */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                setSelectedProduct(null);
                setCategoryFilter('');
              }}
              className={`px-3 py-1.5 rounded-full text-[11px] font-bold transition-all border ${categoryFilter === ''
                ? 'bg-[#6CD800]/10 border-[#6CD800]/30 text-[#6CD800]'
                : 'bg-[#181818] border-[#2b2b2b] text-gray-400 hover:text-white'
                }`}
            >
              All Categories
            </button>
            {Object.entries(ProductCategoryLabels).map(([val, label]) => (
              <button
                key={val}
                onClick={() => {
                  setSelectedProduct(null);
                  setCategoryFilter(val);
                }}
                className={`px-3 py-1.5 rounded-full text-[11px] font-bold transition-all border ${categoryFilter === val
                  ? 'bg-[#6CD800]/10 border-[#6CD800]/30 text-[#6CD800]'
                  : 'bg-[#181818] border-[#2b2b2b] text-gray-400 hover:text-white'
                  }`}
              >
                {label}
              </button>
            ))}
          </div>

          {!selectedProduct ? (
            <>
              {loadingProducts ? (
                <div className="flex flex-col items-center justify-center py-16 space-y-4">
                  <Loader2 className="animate-spin text-[#6CD800]" size={32} />
                  <p className="text-gray-400 text-xs">Loading shop items...</p>
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-16 bg-[#181818] border border-[#2b2b2b] rounded-2xl">
                  <ShoppingBag className="mx-auto text-gray-650 mb-3" size={36} />
                  <p className="text-sm font-bold text-gray-300">No items available in this category</p>
                  <p className="text-xs text-gray-500 mt-1">Check back later or try filtering a different category.</p>
                </div>
              ) : (
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                  padding: "0 12px 80px",
                }}>
                  {products.map((p) => {
                    return (
                      <div
                        key={p.id}
                        onClick={() => setSelectedProduct(p)}
                        style={{
                          background: "#111",
                          borderRadius: 12,
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                          border: "1px solid #1E1E1E",
                          cursor: "pointer"
                        }}
                      >
                        {/* Image container — fills card width */}
                        <div
                          style={{
                            position: "relative",
                            width: "100%",
                            height: 130,
                            background: "#1A1A1A",
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {p.imageUrl ? (
                            <img
                              src={p.imageUrl}
                              alt={p.name}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <ShoppingBag size={24} className="text-gray-750" />
                          )}

                          {/* Stock Status Tag */}
                          <div style={{ position: "absolute", top: 6, right: 6 }}>
                            {getStockStatusBadge(p.stockStatus === 'InStock' || p.stockStatus === '1' ? 1 : p.stockStatus === 'OutOfStock' || p.stockStatus === '2' ? 2 : 3)}
                          </div>

                          {/* Category badge */}
                          <span
                            style={{
                              position: "absolute",
                              bottom: 6,
                              left: 6,
                              background: "rgba(0,0,0,0.7)",
                              color: "#888",
                              fontSize: 9,
                              fontWeight: 700,
                              letterSpacing: 0.8,
                              padding: "2px 6px",
                              borderRadius: 4,
                            }}
                          >
                            {ProductCategoryLabels[p.category] || 'Item'}
                          </span>
                        </div>

                        {/* Content area below image */}
                        <div
                          style={{
                            padding: "8px 10px 10px",
                            display: "flex",
                            flexDirection: "column",
                            gap: 4,
                            flex: 1,
                          }}
                        >
                          {/* Product name — clamp to 2 lines max */}
                          <h4
                            style={{
                              color: "#FFF",
                              fontWeight: 700,
                              fontSize: 13,
                              lineHeight: 1.3,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              height: 34
                            }}
                          >
                            {p.name}
                          </h4>

                          {/* Price Row 1: ₹ Price Reference (Optional) */}
                          {p.sellingPrice && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                marginTop: 2
                              }}
                            >
                              <span style={{ fontSize: 13, color: "#FFF", fontWeight: 750 }}>
                                ₹{p.sellingPrice}
                              </span>
                              {p.mrpPrice && (
                                <span style={{ fontSize: 10, color: "#666", textDecoration: "line-through" }}>
                                  ₹{p.mrpPrice}
                                </span>
                              )}
                              {p.discountPercent && p.discountPercent > 0 && (
                                <span
                                  style={{
                                    fontSize: 9,
                                    fontWeight: 700,
                                    color: "#6CD800",
                                    background: "#162800",
                                    padding: "1px 4px",
                                    borderRadius: 4,
                                    border: "1px solid rgba(108, 216, 0, 0.15)"
                                  }}
                                >
                                  {p.discountPercent}% OFF
                                </span>
                              )}
                            </div>
                          )}

                          {/* Price Row 2: Coin Price */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                              marginTop: 2
                            }}
                          >
                            <span
                              style={{
                                fontSize: 12,
                                color: "#6CD800",
                                fontWeight: 800,
                              }}
                            >
                              🪙 {p.coinCost.toLocaleString()} Coins
                            </span>
                          </div>

                          {/* Price Row 3: Muted Bonus Offer Text */}
                          {p.bonusOffer && (
                            <p
                              style={{
                                color: "#444",
                                fontSize: 9,
                                fontWeight: 600,
                                marginTop: 2,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                              }}
                            >
                              🎁 {p.bonusOffer}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <ProductDetail
              product={selectedProduct}
              onBack={() => setSelectedProduct(null)}
              userCoins={balance}
              setConfirmRedeem={setConfirmRedeem}
            />
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          {loadingHistory ? (
            <div className="flex flex-col items-center justify-center py-16 space-y-4">
              <Loader2 className="animate-spin text-[#6CD800]" size={32} />
              <p className="text-gray-400 text-xs">Loading transaction history...</p>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-16 bg-[#181818] border border-[#2b2b2b] rounded-2xl">
              <History className="mx-auto text-gray-650 mb-3" size={36} />
              <p className="text-sm font-bold text-gray-300">No redemptions found</p>
              <p className="text-xs text-gray-500 mt-1">Spend your coins on products to claim items here.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((h) => {
                const isFulfilled = h.status === 'Fulfilled';
                return (
                  <div
                    key={h.id}
                    className="bg-[#181818] border border-[#2b2b2b] p-4 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-lg bg-[#101010] flex items-center justify-center border border-gray-800 overflow-hidden shrink-0">
                        {h.productImage ? (
                          <img src={h.productImage} alt={h.productName} className="object-contain max-h-full max-w-full" />
                        ) : (
                          <ShoppingBag size={18} className="text-gray-600" />
                        )}
                      </div>
                      <div>
                        <h4 className="font-bold text-white text-sm line-clamp-1">{h.productName}</h4>
                        <div className="flex items-center gap-2 mt-1 text-[10px] font-semibold text-gray-400">
                          <span className="flex items-center gap-0.5">🪙 {h.coinsSpent} Coins</span>
                          <span>•</span>
                          <span>{new Date(h.redeemedAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:items-end gap-2 shrink-0">
                      {/* Status */}
                      <div className="flex items-center gap-1">
                        {isFulfilled ? (
                          <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-1">
                            <CheckCircle2 size={10} /> Fulfilled
                          </span>
                        ) : (
                          <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 flex items-center gap-1">
                            <Clock size={10} /> Pending Claim
                          </span>
                        )}
                      </div>

                      {/* Redemption code display */}
                      {!isFulfilled && (
                        <div className="flex items-center gap-2 bg-[#101010] px-3 py-1 rounded-lg border border-gray-800">
                          <span className="text-[10px] text-gray-500 font-bold uppercase">Claim Code:</span>
                          <span className="text-xs font-bold text-[#6CD800] font-mono select-all uppercase">{h.redemptionCode}</span>
                          <button onClick={() => copyCode(h.redemptionCode)} className="text-gray-550 hover:text-white transition-colors" title="Copy code">
                            <Tag size={11} />
                          </button>
                        </div>
                      )}

                      {isFulfilled && h.fulfilledAt && (
                        <span className="text-[9px] text-gray-500">
                          Fulfilled on {new Date(h.fulfilledAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmRedeem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-[#181818] border border-[#2b2b2b] rounded-2xl p-6 text-center space-y-4 shadow-xl">
            <ShoppingBag className="mx-auto text-yellow-400" size={32} />
            <div className="space-y-1">
              <h3 className="text-lg font-bold text-white uppercase tracking-wide">Redeem Item</h3>
              <p className="text-xs text-gray-400">
                Are you sure you want to spend <span className="text-[#6CD800] font-black">🪙 {confirmRedeem.coinCost} Coins</span> on:
              </p>
              <h4 className="text-sm font-extrabold text-white mt-2 font-bebas tracking-wider">{confirmRedeem.name}</h4>
            </div>

            <div className="flex gap-3 pt-3">
              <button
                onClick={() => setConfirmRedeem(null)}
                className="flex-1 py-2 rounded-lg border border-gray-700 hover:bg-[#222] text-gray-300 text-xs font-bold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleRedeem}
                disabled={redeeming}
                className="flex-1 py-2 bg-[#6CD800] text-[#0B0F19] hover:bg-[#5bc200] rounded-lg text-xs font-black uppercase flex items-center justify-center gap-1.5 transition-all"
              >
                {redeeming && <Loader2 className="animate-spin" size={13} />}
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {redeemSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md">
          <div className="w-full max-w-sm bg-[#0F172A] border border-[#1E293B] rounded-2xl p-6 text-center space-y-4 relative shadow-2xl">
            <button
              onClick={() => setRedeemSuccess(null)}
              className="absolute top-4 right-4 text-gray-450 hover:text-white p-1 rounded-full hover:bg-gray-800 transition-colors"
            >
              <X size={16} />
            </button>

            <div className="h-12 w-12 rounded-full bg-[#6CD800]/10 border border-[#6CD800]/20 flex items-center justify-center mx-auto text-[#6CD800]">
              <CheckCircle2 size={24} />
            </div>

            <div className="space-y-1.5">
              <h3 className="text-lg font-bold text-white uppercase tracking-wide font-bebas">Redeemed Successfully!</h3>
              <p className="text-[11px] text-gray-400">
                Show the code below to the gym receptionist to collect your item:
              </p>

              {/* Code display */}
              <div className="bg-[#0B0F19] p-3 rounded-lg border border-gray-800 flex items-center justify-between max-w-[240px] mx-auto mt-3">
                <span className="text-sm font-black text-[#6CD800] tracking-widest font-mono uppercase select-all flex-1">
                  {redeemSuccess.redemptionCode}
                </span>
                <button
                  onClick={() => copyCode(redeemSuccess.redemptionCode)}
                  className="text-gray-400 hover:text-white p-1 rounded transition-colors"
                  title="Copy code"
                >
                  <Copy size={13} />
                </button>
              </div>
              {copied && <span className="text-[9px] text-[#6CD800] font-semibold block mt-1">Code copied to clipboard!</span>}

              <h4 className="text-xs font-bold text-white mt-4 block">Item: {redeemSuccess.productName}</h4>
              <span className="text-[10px] text-gray-500 block">Spent 🪙 {redeemSuccess.coinsSpent} Coins</span>
            </div>

            <button
              onClick={() => {
                setRedeemSuccess(null);
                setActiveTab('history');
              }}
              className="w-full py-2.5 bg-[#6CD800] text-[#0B0F19] rounded-lg text-xs font-black tracking-wider uppercase hover:bg-[#5bc200] transition-colors mt-2"
            >
              View Redemption Orders
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Store;
